/**
 * Favicon Utilities
 *
 * A small utility module for dynamically updating and resetting the browser favicon.
 * This is useful for reflecting application state changes (e.g., branding changes)
 * without requiring a full page reload.
 *
 * ## Features:
 * - **Dynamic Favicon Update**: Change the favicon at runtime using a custom URL.
 * - **Cache Busting**: Automatically appends a timestamp query parameter to force the browser
 *   to reload the favicon instead of using a cached version.
 * - **Reset to Default**: Easily revert the favicon back to the default SVG or PNG.
 * - **Multi-Link Support**: Handles multiple `<link rel="icon">` elements if present.
 *
 * ## Internal Helpers:
 * - `getFaviconLinks`: Retrieves all favicon `<link>` elements from the document.
 *
 * ## Methods:
 *
 * ### `resetFavicon`
 * Resets the favicon to the default application icons.
 * - Uses `/favicon.svg` if the link type is `image/svg+xml`
 * - Falls back to `/favicon.png` for other types
 * - Appends a timestamp to prevent caching
 *
 * ### `setFavicon`
 * Updates the favicon to a custom URL.
 * - Accepts a new `href` string
 * - Applies the update to all favicon link elements
 * - Appends a timestamp to ensure the browser refreshes the icon
 *
 * ## Example Usage:
 * ```ts
 * import { setFavicon, resetFavicon } from './favicon'
 *
 * // Set a custom favicon
 * setFavicon('/icons/alert.png')
 *
 * // Reset to default favicon
 * resetFavicon()
 * ```
 */

/**
 * Retrieves all favicon link elements from the document.
 *
 * @returns {NodeListOf<HTMLLinkElement>} A list of `<link rel="icon">` elements.
 */

const getFaviconLinks = (): NodeListOf<HTMLLinkElement> => {
  return document.querySelectorAll<HTMLLinkElement>('link[rel~=\'icon\']')
}

/**
 * Resets all favicon links to the default application favicon.
 *
 * - Uses SVG favicon if the link type is `image/svg+xml`
 * - Otherwise falls back to PNG
 * - Appends a timestamp query parameter to prevent caching
 */
export const resetFavicon = (): void => {
  const date = Date.now()

  getFaviconLinks().forEach(link => {
    if (link.type === 'image/svg+xml') {
      link.href = `/favicon.svg?v=${date}`
      return
    }

    link.href = `/favicon.png?v=${date}`
  })
}

/**
 * Sets all favicon links to a custom favicon URL.
 *
 * @param {string} href - The path or URL of the new favicon.
 *
 * - Applies to all `<link rel="icon">` elements
 * - Appends a timestamp query parameter to force refresh
 */
export const setFavicon = (href: string): void => {
  const date = Date.now()

  getFaviconLinks().forEach(link => {
    link.href = `${href}?v=${date}`
  })
}
